import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { TextContent } from '../../../models/text-content.model';
import { TextContentService } from '../../../services/text-content.service';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { PaginationService } from '../../../../shared/services/pagination.service';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';

@Component({
  selector: 'app-text-content-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    BreadcrumbModule,
    ToolbarModule,
    ButtonModule,
    RouterLink,
    ProgressSpinnerModule,
    ToastModule,
    TagStatusComponent,
  ],
  templateUrl: './text-content-list.component.html',
})
export class TextContentListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private textContentService = inject(TextContentService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  textContents = signal<TextContent[]>([]);

  pageNumber = signal(1);
  pageSize = signal(10);
  totalRecords = signal(0);
  totalPages = signal(0);

  loading = signal(false);

  slugFilter?: string;
  contentFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize()
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.textContentService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.slugFilter,
        this.contentFilter
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.textContents.set(page.items as TextContent[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os textos',
              life: 3000,
            });
          }
        },
      });
  }

  getContentPreview(content: string): string {
    const plainText = (content || '').replace(/<[^>]+>/g, '').trim();
    if (!plainText) return '-';
    return plainText.length > 100
      ? `${plainText.substring(0, 100)}...`
      : plainText;
  }

  getStatusDescription(isActive: boolean): string {
    return isActive ? 'Ativo' : 'Inativo';
  }
}
